import { useContext, useEffect, useRef, useState } from 'react';
import { useCatalogApi } from '@/webapi/use-catalog-api';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { toPageKind } from '@/features/ai-focused-editor/context/use-ai-catalog-search';
import {
  handleToUrl,
  PageKind,
  StorePage,
} from '@/features/editor/context/use-device-navigation';

const REMOTE_SEARCH_LIMIT = 25;
const UNRANKED = 999;

function searchTargetFor(kind: PageKind) {
  switch (kind) {
    case PageKind.PRODUCT:
      return { path: `products`, label: `Product` };
    case PageKind.COLLECTION:
      return { path: `collections`, label: `Category` };
    default:
      return undefined;
  }
}

export function useRemotePageSearch(
  search: string,
  allowedKinds: Set<PageKind>,
): StorePage[] {
  const { account } = useContext(AccountContext);
  const ignoreRootPath = useFeatureBit(FeatureBit.EDITOR_URLS_IGNORE_ROOT_PATH);
  const { searchCatalog } = useCatalogApi();
  const [pages, setPages] = useState<StorePage[]>([]);
  const latestSearch = useRef(search);

  const toStorePage = (page: StorePage): StorePage | undefined => {
    const kind = toPageKind(page);
    const target = searchTargetFor(kind);
    const handle = lastPathSegment(page.url);
    if (!target || !allowedKinds.has(kind) || !handle) return undefined;

    return {
      url: handleToUrl(account, target.path, handle, ignoreRootPath),
      caption: `${page.caption} (${target.label})`,
      kind,
      rank: UNRANKED,
    };
  };

  useEffect(() => {
    latestSearch.current = search;
    setPages([]);
    if (!isSearchable(search)) return;

    searchCatalog(search, REMOTE_SEARCH_LIMIT, account?.store?.domain)
      .then((res) => {
        if (latestSearch.current !== search) return;
        setPages((res?.pages || []).map(toStorePage).filter(Boolean));
      })
      .catch(() => setPages([]));
  }, [search]);

  return pages;
}

function isSearchable(search: string) {
  const query = search?.trim();
  return !!query && !query.startsWith(`http`);
}

function lastPathSegment(url: string) {
  try {
    return new URL(url).pathname.split(`/`).filter(Boolean).pop();
  } catch (e) {
    return undefined;
  }
}
